import React, { useState } from "react";
import { array, identity, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { EditorState } from "draft-js";
import { TStaffUser } from "../../../../domain/codecs/User";
import { TCaseTimelineRoadblockContext } from "../../../../domain/codecs/form/CaseTimelineForm";
import { TChatMessageAction, isChatMessageAction, isSignOffInvalidationAction } from "../../../../domain/codecs/form/RoadblockForm";
import { TTimelineNoteCluster } from "./CRMCaseTimelineGroupingLogic";
import { CRMRoadblockRichTextArea, HTML__TextAreaWithCommandsEditorContent } from "../CRMRoadblock/CRMRoadblockRichTextArea/CRMRoadblockRichTextArea";
import { getResetEditorState } from "../CRMRoadblock/CRMRoadblockRichTextArea/DraftJsUtils/getResetEditorState";
import { CRMUserAvatar } from "../Complex/CRMUserAvatar/CRMUserAvatar";
import { CRMIconAvatar } from "../Complex/CRMIconAvatar/CRMIconAvatar";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { Background } from "../BuildingBlocks/Background";
import { Padding } from "../BuildingBlocks/Padding";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { Text } from "../BuildingBlocks/Text";
import { CRMIcon } from "../CRMIcon/CRMIcon";
import { CRMColors } from "../../models/CRMColors";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMSystemLastName, CRMSystemName } from "../../models/CRMSystem";
import { findMostDistantDate, formatDateCurried } from "../../../../shared/src/utilsByDomain/dateTime";
import { capitaliseFirst, underscoreCaseToNormalCase } from "../../../../shared/src/utilsByDomain/string";

export type TTimelineNoteEdit = { id: string; chats_id: string; note_body: string };
export type TTimelineNotePin = { id: string; chats_id: string; pinned: boolean };

type TCaseTimelineNoteProps = {
    participants: Array<TStaffUser>;
    cluster: TTimelineNoteCluster;
    currentUserId: string;
    onOpenRoadblock: (roadblock: TCaseTimelineRoadblockContext) => void;
    onEditNote: (payload: TTimelineNoteEdit) => void;
    onPinNote: (payload: TTimelineNotePin) => void;
};

const signOffText = (user: string | null, reason: string): string =>
    `I've removed the previous Sign Off when ${user || "we"} ${reason || "made changes"}. A new Sign Off will need to be made.`;

export const CRMCaseTimelineNote = (props: React.PropsWithChildren<TCaseTimelineNoteProps>): JSX.Element => {

    const authorId = props.cluster.entries[0].action.created_by;
    const isSystem = authorId === null;

    const author: TStaffUser = pipe(
        props.participants,
        array.findFirst((participant) => participant.id === authorId),
        option.fold(
            () => ({ id: "", first_name: CRMSystemName, last_name: CRMSystemLastName, email: "" }),
            identity.flatten
        )
    );

    const timestamp = pipe(
        props.cluster.entries,
        array.map((entry) => entry.action_date),
        findMostDistantDate,
        formatDateCurried("h:mm a"),
        (date) => date.toLowerCase()
    );

    const roadblockName = pipe(
        underscoreCaseToNormalCase(props.cluster.roadblock.roadblock_type),
        capitaliseFirst
    );

    return (
        <Padding type="custom" spacing={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}>
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="flex-start" childSize="auto 1fr">
                {/* AVATAR */}
                {isSystem
                    ? <CRMIconAvatar icon="robot" size="small" iconColor={CRMColors.PRIMARY_2} backgroundColor={CRMColors.HIGHLIGHTS_CALMING_PINK_10} />
                    : <CRMUserAvatar size="small" user={author} />
                }

                {/* BODY */}
                <SpacingColumn spacing="3px">
                    <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                        <FontQuicksand>
                            <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_INK} weight={700}>
                                {author.first_name}
                            </Text>
                        </FontQuicksand>
                        <FontQuicksand>
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4} weight={500}>
                                {timestamp}
                            </Text>
                        </FontQuicksand>
                    </SpacingRow>

                    <SpacingColumn spacing="2px">
                        {props.cluster.entries.map((entry, index) => {
                            if (isChatMessageAction(entry)) {
                                return (
                                    <NoteMessageLine
                                        key={index}
                                        message={entry.action}
                                        chatsId={props.cluster.roadblock.chats_id}
                                        canEdit={!isSystem && entry.action.created_by === props.currentUserId}
                                        onEditNote={props.onEditNote}
                                        onPinNote={props.onPinNote}
                                        showRoadblock={index === 0}
                                        roadblock={props.cluster.roadblock}
                                        roadblockName={roadblockName}
                                        onOpenRoadblock={props.onOpenRoadblock}
                                    />
                                );
                            }
                            if (isSignOffInvalidationAction(entry)) {
                                return (
                                    <div key={index} className="crm-case-timeline-note-text">
                                        <div>{signOffText(entry.action.user, entry.action.sign_off_invalidated_reason)}</div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </SpacingColumn>
                </SpacingColumn>
            </SpacingRow>
        </Padding>
    );
};

type TNoteMessageLineProps = {
    message: TChatMessageAction["action"];
    chatsId: string;
    canEdit: boolean;
    onEditNote: (payload: TTimelineNoteEdit) => void;
    onPinNote: (payload: TTimelineNotePin) => void;
    showRoadblock: boolean;
    roadblock: TCaseTimelineRoadblockContext;
    roadblockName: string;
    onOpenRoadblock: (roadblock: TCaseTimelineRoadblockContext) => void;
};

const NoteMessageLine = (props: React.PropsWithChildren<TNoteMessageLineProps>): JSX.Element => {

    const [isEditing, setIsEditing] = useState(false);
    const [editorState, setEditorState] = useState(() => EditorState.createWithContent(
        HTML__TextAreaWithCommandsEditorContent(props.message.note_body)
    ));

    const isPinned = props.message.pinned_at !== null;
    const wasEdited = props.message.edited_at !== null;

    const startEditing = () => {
        setEditorState(EditorState.createWithContent(HTML__TextAreaWithCommandsEditorContent(props.message.note_body)));
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setEditorState(getResetEditorState(editorState));
        setIsEditing(false);
    };

    const saveEditing = (_newEditorState: EditorState, html: string) => {
        props.onEditNote({ id: props.message.id, chats_id: props.chatsId, note_body: html });
        setIsEditing(false);
    };

    const togglePin = () =>
        props.onPinNote({ id: props.message.id, chats_id: props.chatsId, pinned: !isPinned });

    if (isEditing) {
        return (
            <SpacingColumn spacing={CRMSpacing.TINY}>
                <CRMRoadblockRichTextArea
                    placeholder="Edit your note…"
                    editorState={editorState}
                    commandList={[]}
                    onChange={(state) => setEditorState(state)}
                    onSubmit={saveEditing}
                />
                <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                    <Background borderRadius="6px" padding="2px 6px" cursor="pointer" onClick={cancelEditing}>
                        <FontQuicksand>
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4} weight={600}>
                                Cancel
                            </Text>
                        </FontQuicksand>
                    </Background>
                </SpacingRow>
            </SpacingColumn>
        );
    }

    return (
        <SpacingRow spacing={CRMSpacing.TINY} alignItems="center" childSize="1fr auto">
            <div className="crm-case-timeline-note-text">
                <div dangerouslySetInnerHTML={{ __html: props.message.note_body }} />
                {wasEdited &&
                    <FontQuicksand>
                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4} weight={500}>
                            (edited)
                        </Text>
                    </FontQuicksand>
                }
            </div>

            {/* Icon rail — edit / pin, plus the roadblock pill on the first line.
                All share this row so they line up. */}
            <SpacingRow spacing="2px" alignItems="center">
                {props.canEdit &&
                    <Background
                        borderRadius="6px"
                        padding="3px"
                        cursor="pointer"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        onClick={startEditing}
                    >
                        <CRMIcon iconName="pencil" size="14px" colour="neutral-4" title="Edit note" />
                    </Background>
                }
                <Background
                    borderRadius="6px"
                    padding="3px"
                    cursor="pointer"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={togglePin}
                >
                    <CRMIcon
                        iconName={isPinned ? "pinned" : "pin"}
                        size="14px"
                        colour={isPinned ? "primary-2" : "neutral-4"}
                        title={isPinned ? "Unpin note" : "Pin note"}
                    />
                </Background>
                {props.showRoadblock &&
                    <Background
                        color={CRMColors.NEUTRAL_12}
                        borderRadius="7px"
                        borderDefinition={`solid 1px ${CRMColors.NEUTRAL_10}`}
                        padding="4px"
                        cursor="pointer"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        onClick={() => props.onOpenRoadblock(props.roadblock)}
                    >
                        <CRMIcon iconName="roadblocks" size="15px" colour="neutral-2" title={props.roadblockName} />
                    </Background>
                }
            </SpacingRow>
        </SpacingRow>
    );
};
